// @ts-ignore
import '@mdxeditor/editor/style.css';

import { forwardRef, useRef, useCallback, useEffect, useImperativeHandle } from 'react';
import {
    MDXEditor,
    MDXEditorMethods,
    headingsPlugin,
    listsPlugin,
    quotePlugin,
    thematicBreakPlugin,
    tablePlugin,
    codeBlockPlugin,
    codeMirrorPlugin,
    imagePlugin,
    linkPlugin,
    linkDialogPlugin,
    markdownShortcutPlugin,
    toolbarPlugin,
    UndoRedo,
    BoldItalicUnderlineToggles,
    BlockTypeSelect,
    CreateLink,
    InsertTable,
    InsertThematicBreak,
    ListsToggle,
    InsertCodeBlock,
    CodeToggle,
} from '@mdxeditor/editor';

import Box from '@mui/material/Box';
import { useTheme, alpha } from '@mui/material/styles';

export interface RichTextEditorProps {
    value?: string;
    onChange?: (markdown: string) => void;
    placeholder?: string;
    minHeight?: number | string;
    readOnly?: boolean;
    compact?: boolean;
    autoFocus?: boolean;
    sx?: object;
    onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

export const RichTextEditor = forwardRef<MDXEditorMethods, RichTextEditorProps>(
    (
        {
            value = '',
            onChange,
            placeholder = '',
            minHeight = 100,
            readOnly = false,
            compact = false,
            autoFocus = false,
            sx = {},
            onFocus,
            onBlur,
            onKeyDown,
        },
        ref
    ) => {
        const theme = useTheme();
        const isDark = theme.palette.mode === 'dark';
        const boxRef = useRef<HTMLDivElement>(null);
        const editorRef = useRef<MDXEditorMethods>(null);
        const lastMarkdownRef = useRef(value);

        useImperativeHandle(ref, () => editorRef.current as MDXEditorMethods);

        useEffect(() => {
            if (value !== lastMarkdownRef.current) {
                editorRef.current?.setMarkdown(value);
                lastMarkdownRef.current = value;
            }
        }, [value]);

        const handleChange = useCallback((markdown: string) => {
            lastMarkdownRef.current = markdown;
            onChange?.(markdown);
        }, [onChange]);

        // Auto-focus contenteditable when autoFocus is true, cursor at end
        useEffect(() => {
            if (!autoFocus || readOnly) return;
            const timer = setTimeout(() => {
                const ce = boxRef.current?.querySelector('[contenteditable="true"]') as HTMLElement | null;
                if (ce) {
                    ce.focus();
                    // Move cursor to end
                    const range = document.createRange();
                    const sel = window.getSelection();
                    if (ce.childNodes.length > 0) {
                        range.selectNodeContents(ce);
                        range.collapse(false); // collapse to end
                    } else {
                        range.setStart(ce, 0);
                        range.collapse(true);
                    }
                    sel?.removeAllRanges();
                    sel?.addRange(range);
                }
            }, 50);
            return () => clearTimeout(timer);
        }, [autoFocus, readOnly]);

        // When a toolbar button gets focus via Tab, redirect to contenteditable
        // so Tab goes straight to the writing area
        useEffect(() => {
            const box = boxRef.current;
            if (!box || readOnly) return;
            const handler = (e: FocusEvent) => {
                const target = e.target as HTMLElement;
                // Only redirect toolbar button focus (Tab into toolbar)
                if (target.closest('[class*="_toolbar_"]') && target.matches('button, [role="button"]')) {
                    // Check if focus is coming from outside the Box (Tab in)
                    if (!box.contains(e.relatedTarget as Node)) {
                        const ce = box.querySelector('[contenteditable="true"]') as HTMLElement | null;
                        if (ce) {
                            ce.focus();
                            const range = document.createRange();
                            const sel = window.getSelection();
                            if (ce.childNodes.length > 0) {
                                range.selectNodeContents(ce);
                                range.collapse(false);
                            } else {
                                range.setStart(ce, 0);
                                range.collapse(true);
                            }
                            sel?.removeAllRanges();
                            sel?.addRange(range);
                        }
                    }
                }
            };
            box.addEventListener('focusin', handler);
            return () => box.removeEventListener('focusin', handler);
        }, [readOnly]);

        // Click su spazio morto → focus sul contenteditable
        const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;
            // Se il click è già sul contenteditable o sulla toolbar, non interferire
            if (target.closest('[contenteditable="true"]')) return;
            if (target.closest('[class*="_toolbar_"]')) return;
            if (target.closest('button, a, input, select, textarea')) return;

            const ce = boxRef.current?.querySelector('[contenteditable="true"]') as HTMLElement | null;
            if (ce) ce.focus();
        }, []);

        const primary = theme.palette.primary.main;
        const bgHover = theme.palette.action.hover;
        const textPrimary = theme.palette.text.primary;
        const textSecondary = theme.palette.text.secondary;
        const divider = theme.palette.divider;
        const editorBackground = theme.palette.background.paper;
        const radius = theme.shape.borderRadius;

        const imageUploadHandler = async (image: File): Promise<string> => {
            return URL.createObjectURL(image);
        };

        const plugins = compact
            ? [
                listsPlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                markdownShortcutPlugin(),
                ...(!readOnly
                    ? [
                        toolbarPlugin({
                            toolbarContents: () => (
                                <>
                                    <UndoRedo />
                                    <BoldItalicUnderlineToggles />
                                    <ListsToggle />
                                    <CreateLink />
                                </>
                            ),
                        }),
                    ]
                    : []),
            ]
            : [
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                thematicBreakPlugin(),
                tablePlugin(),
                linkDialogPlugin(),
                codeBlockPlugin({ defaultCodeBlockLanguage: 'typescript' }),
                codeMirrorPlugin({
                    codeBlockLanguages: {
                        typescript: 'TypeScript',
                        javascript: 'JavaScript',
                        tsx: 'TSX',
                        jsx: 'JSX',
                        css: 'CSS',
                        html: 'HTML',
                        json: 'JSON',
                        bash: 'Bash',
                        sql: 'SQL',
                        python: 'Python',
                        '': 'Plain text',
                    },
                }),
                imagePlugin({ imageUploadHandler }),
                linkPlugin(),
                markdownShortcutPlugin(),
                ...(!readOnly
                    ? [
                        toolbarPlugin({
                            toolbarContents: () => (
                                <>
                                    <UndoRedo />
                                    <BlockTypeSelect />
                                    <BoldItalicUnderlineToggles />
                                    <CodeToggle />
                                    <ListsToggle />
                                    <CreateLink />
                                    <InsertTable />
                                    <InsertCodeBlock />
                                    <InsertThematicBreak />
                                </>
                            ),
                        }),
                    ]
                    : []),
            ];

        return (
            <Box
                ref={boxRef}
                onClick={handleClick}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                sx={{
                    border: `1px solid ${divider}`,
                    borderRadius: `${radius}px`,
                    backgroundColor: editorBackground,
                    color: textPrimary,
                    overflow: 'visible',
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:focus-within': {
                        borderColor: primary,
                        boxShadow: `0 0 0 1px ${alpha(primary, 0.25)}`,
                    },

                    // ── MDXEditor root ─────────────────────────────────────────
                    '& [class*="_rootContentEditableWrapper_"]': {
                        minHeight,
                        cursor: 'text',
                        transition: 'min-height 0.3s ease-in-out',
                        color: textPrimary,
                        backgroundColor: `${editorBackground} !important`,
                    },
                    '& [class*="_root_"]': {
                        fontSize: '0.8125rem',
                        fontFamily: theme.typography.fontFamily,
                        color: `${textPrimary} !important`,
                        backgroundColor: `${editorBackground} !important`,
                    },
                    '& .mdxeditor, & .mdxeditor-root-contenteditable, & [class*="_contentEditable_"]': {
                        color: `${textPrimary} !important`,
                        backgroundColor: `${editorBackground} !important`,
                    },

                    // ── Toolbar ────────────────────────────────────────────────
                    '& .mdxeditor-toolbar, & [class*="_toolbar_"]': {
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '0px',
                        minHeight: 'unset',
                        padding: '2px 4px',
                        backgroundColor: isDark
                            ? '#000000 !important'
                            : alpha(theme.palette.grey[100], 0.6),
                        color: isDark ? '#ffffff !important' : textSecondary,
                        borderBottom: `1px solid ${isDark ? '#ffffff' : divider}`,
                    },

                    // ── Toolbar buttons ───────────────────────────────────────
                    '& .mdxeditor-toolbar button, & .mdxeditor-toolbar [role="button"], & [class*="_toolbarButton_"], & [class*="_toolbarToggleItem_"]': {
                        borderRadius: '3px',
                        color: isDark ? '#ffffff !important' : textSecondary,
                        padding: '2px 5px',
                        minHeight: 24,
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        '&:hover': {
                            backgroundColor: isDark ? `${alpha('#ffffff', 0.14)} !important` : alpha(primary, 0.08),
                            color: isDark ? '#ffffff !important' : primary,
                        },
                        '& svg': {
                            fontSize: '1rem',
                            color: isDark ? '#ffffff !important' : undefined,
                            fill: isDark ? '#ffffff !important' : undefined,
                            stroke: isDark ? '#ffffff !important' : undefined,
                        },
                    },
                    '& [class*="_activeToolbarButton_"], & [data-state="on"], & [aria-pressed="true"]': {
                        backgroundColor: `${isDark ? alpha('#ffffff', 0.18) : alpha(primary, 0.12)} !important`,
                        color: `${isDark ? '#ffffff' : primary} !important`,
                    },

                    // ── Select trigger (nella toolbar) ────────────────────────
                    '& .mdxeditor-toolbar button, & [class*="_selectTrigger_"]': {
                        minHeight: 24,
                        fontSize: '0.75rem',
                        padding: '0 6px',
                        borderRadius: '3px',
                        color: isDark ? '#ffffff !important' : undefined,
                        backgroundColor: isDark ? '#000000 !important' : undefined,
                        '& svg': {
                            color: isDark ? '#ffffff !important' : undefined,
                            fill: isDark ? '#ffffff !important' : undefined,
                            stroke: isDark ? '#ffffff !important' : undefined,
                        },
                    },

                    // ── Separator ────────────────────────────────────────────
                    '& .mdxeditor-toolbar [role="separator"], & [class*="_toolbarSeparator_"]': {
                        backgroundColor: isDark ? '#ffffff' : divider,
                        margin: '2px 4px',
                        width: '1px',
                        height: '16px',
                    },

                    // ── Content editable ────────────────────────────────────────
                    '& [contenteditable]': {
                        minHeight,
                        transition: 'min-height 0.3s ease-in-out',
                        padding: '8px 12px',
                        fontSize: '0.8125rem',
                        fontFamily: theme.typography.fontFamily,
                        color: `${textPrimary} !important`,
                        backgroundColor: `${editorBackground} !important`,
                        lineHeight: 1.6,
                        outline: 'none',
                        '&:empty::before': {
                            content: `"${placeholder}"`,
                            color: textSecondary,
                            pointerEvents: 'none',
                            float: 'left',
                        },
                    },
                    '& [contenteditable] p, & [contenteditable] span, & [contenteditable] li, & [contenteditable] strong, & [contenteditable] em, & [contenteditable] u, & [contenteditable] s, & [contenteditable] td': {
                        color: 'inherit !important',
                        backgroundColor: 'transparent',
                    },

                    // ── Headings ──────────────────────────────────────────────
                    '& [contenteditable] h1': { fontSize: '1.3rem', fontWeight: 600, margin: '0.5em 0 0.2em', lineHeight: 1.3, color: textPrimary },
                    '& [contenteditable] h2': { fontSize: '1.05rem', fontWeight: 600, margin: '0.4em 0 0.15em', lineHeight: 1.3, color: textPrimary },
                    '& [contenteditable] h3': { fontSize: '0.9rem', fontWeight: 600, margin: '0.3em 0 0.1em', lineHeight: 1.3, color: textPrimary },
                    '& [contenteditable] p': { margin: '0.2em 0' },

                    // ── Blockquote ────────────────────────────────────────────
                    '& [contenteditable] blockquote': {
                        borderLeft: `3px solid ${alpha(primary, 0.5)}`,
                        margin: '0.3em 0',
                        padding: '2px 10px',
                        color: textSecondary,
                        fontStyle: 'italic',
                    },

                    // ── Inline code ───────────────────────────────────────────
                    '& [contenteditable] code': {
                        fontFamily: "'Fira Code', 'Consolas', monospace",
                        fontSize: '0.85em',
                        backgroundColor: alpha(isDark ? '#fff' : '#000', 0.06),
                        borderRadius: '3px',
                        padding: '1px 4px',
                    },

                    // ── Lists ─────────────────────────────────────────────────
                    '& [contenteditable] ul, & [contenteditable] ol': { paddingLeft: '1.3em', margin: '0.2em 0' },
                    '& [contenteditable] li': { margin: '0.05em 0' },

                    // ── Tables ────────────────────────────────────────────────
                    '& [contenteditable] table': { borderCollapse: 'collapse', width: '100%', margin: '0.4em 0', fontSize: '0.8125rem' },
                    '& [contenteditable] th, & [contenteditable] td': { border: `1px solid ${divider}`, padding: '4px 8px', textAlign: 'left', verticalAlign: 'top' },
                    '& [contenteditable] th': { backgroundColor: bgHover, fontWeight: 600 },

                    // ── HR ────────────────────────────────────────────────────
                    '& [contenteditable] hr': { border: 'none', borderTop: `1px solid ${divider}`, margin: '0.5em 0' },

                    // ── CodeMirror ────────────────────────────────────────────
                    '& .cm-editor': { borderRadius: `${radius - 1}px`, fontSize: '0.8125rem', border: `1px solid ${divider}`, margin: '0.3em 0', color: textPrimary, backgroundColor: editorBackground },
                    '& .cm-content, & .cm-line': { color: textPrimary, backgroundColor: editorBackground },
                    '& .cm-focused': { outline: 'none' },

                    // ── Links ────────────────────────────────────────────────
                    '& [contenteditable] a': { color: primary, textDecoration: 'underline' },

                    ...sx,
                }}
            >
                <MDXEditor
                    ref={editorRef}
                    markdown={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    plugins={plugins}
                />
            </Box>
        );
    }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
